import { useCallback, useEffect, useState } from "react";
import { ensureEndNotBeforeStart, parseDateTimeOrNull } from "@/lib/date-utils";
import { buildUpdateEventPayloadFromEvent } from "@/lib/event-utils";
import { useAdminEventUpdate } from "@/lib/admin-api";
import { confirmAsync, type ToastMessage } from "@/lib/ui";
import type { AdminEvent, AdminEventSession } from "../types";

type SessionDraft = {
  startsAt: Date | null;
  endsAt: Date | null;
  label: string;
};

type UseEventSessionsArgs = {
  event: AdminEvent | null;
  eventId: string | undefined;
  refreshEvent: () => Promise<void>;
  showToast: (message: ToastMessage) => void;
};

export function useEventSessions(args: UseEventSessionsArgs) {
  const { event, eventId, refreshEvent, showToast } = args;
  const updateMutation = useAdminEventUpdate();

  const [sessionDialogVisible, setSessionDialogVisible] = useState(false);
  const [editingSession, setEditingSession] = useState<AdminEventSession | null>(
    null,
  );
  const [newSession, setNewSession] = useState<SessionDraft>({
    startsAt: null,
    endsAt: null,
    label: "",
  });

  const handleAddSession = useCallback(() => {
    setEditingSession(null);
    setNewSession({ startsAt: null, endsAt: null, label: "" });
    setSessionDialogVisible(true);
  }, []);

  const handleEditSession = useCallback((session: AdminEventSession) => {
    setEditingSession(session);
    setNewSession({
      startsAt: parseDateTimeOrNull(session.startsAt),
      endsAt: parseDateTimeOrNull(session.endsAt),
      label: session.label || "",
    });
    setSessionDialogVisible(true);
  }, []);

  const performRemoveSession = useCallback(
    async (sessionId: string) => {
      if (!event || !eventId) return;

      const updatedSessions = event.sessions.filter((s) => s.id !== sessionId);

      try {
        const payload = buildUpdateEventPayloadFromEvent(event, {
          sessions: updatedSessions,
        });

        await updateMutation.mutateAsync({
          params: { path: { eventId } },
          body: payload,
        });

        showToast({
          severity: "success",
          summary: "Success",
          detail: "Session removed successfully",
        });

        await refreshEvent();
      } catch (error) {
        showToast({
          severity: "error",
          summary: "Error",
          detail:
            error instanceof Error ? error.message : "Failed to remove session",
        });
      }
    },
    [event, eventId, refreshEvent, showToast, updateMutation],
  );

  const confirmAndRemoveSession = useCallback(
    async (sessionId: string) => {
      const confirmed = await confirmAsync({
        message: "Are you sure you want to delete this session?",
        header: "Confirm Deletion",
        icon: "pi pi-exclamation-triangle",
        acceptLabel: "Delete",
        rejectLabel: "Cancel",
        defaultFocus: "reject",
      });
      if (!confirmed) return;
      await performRemoveSession(sessionId);
    },
    [performRemoveSession],
  );

  const handleRemoveSession = useCallback(
    (sessionId: string) => {
      if (!event || !eventId) return;

      const updatedSessions = event.sessions.filter((s) => s.id !== sessionId);
      if (updatedSessions.length === 0) {
        showToast({
          severity: "error",
          summary: "Error",
          detail: "Event must have at least one session",
        });
        return;
      }

      void confirmAndRemoveSession(sessionId);
    },
    [confirmAndRemoveSession, event, eventId, showToast],
  );

  const handleSaveSession = useCallback(async () => {
    if (!event || !eventId || !newSession.startsAt || !newSession.endsAt) {
      showToast({
        severity: "error",
        summary: "Error",
        detail: "Start and end times are required",
      });
      return;
    }

    if (newSession.endsAt <= newSession.startsAt) {
      showToast({
        severity: "error",
        summary: "Error",
        detail: "End time must be after start time",
      });
      return;
    }

    try {
      let updatedSessions = [...event.sessions];
      if (editingSession) {
        const index = updatedSessions.findIndex((s) => s.id === editingSession.id);
        if (index >= 0) {
          updatedSessions[index] = {
            ...updatedSessions[index],
            startsAt: newSession.startsAt.toISOString(),
            endsAt: newSession.endsAt.toISOString(),
            label: newSession.label || null,
          };
        }
      } else {
        updatedSessions.push({
          id: "",
          eventId: event.id,
          startsAt: newSession.startsAt.toISOString(),
          endsAt: newSession.endsAt.toISOString(),
          label: newSession.label || null,
          createdAt: null,
        });
      }

      const payload = buildUpdateEventPayloadFromEvent(event, {
        sessions: updatedSessions,
      });

      await updateMutation.mutateAsync({
        params: { path: { eventId } },
        body: payload,
      });

      showToast({
        severity: "success",
        summary: "Success",
        detail: editingSession
          ? "Session updated successfully"
          : "Session added successfully",
      });

      setSessionDialogVisible(false);
      await refreshEvent();
    } catch (error) {
      showToast({
        severity: "error",
        summary: "Error",
        detail: error instanceof Error ? error.message : "Failed to save session",
      });
    }
  }, [
    editingSession,
    event,
    eventId,
    newSession.endsAt,
    newSession.label,
    newSession.startsAt,
    refreshEvent,
    showToast,
    updateMutation,
  ]);

  // Ensure end time never precedes start time when editing the draft session.
  useEffect(() => {
    setNewSession((prev) => ({
      ...prev,
      endsAt: ensureEndNotBeforeStart(prev.startsAt, prev.endsAt),
    }));
  }, [newSession.startsAt]);

  return {
    sessionDialogVisible,
    setSessionDialogVisible,
    editingSession,
    newSession,
    setNewSession,
    handleAddSession,
    handleEditSession,
    handleRemoveSession,
    handleSaveSession,
  };
}

